import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { PROTOCOL_VERSION } from '@thrasher/shared';
import type {
  AttackKind,
  AwardEvent,
  ClientToServerEvents,
  FeedEvent,
  HelloResult,
  HitEvent,
  RespawnEvent,
  Ride,
  RiderState,
  RosterEntry,
  ServerToClientEvents,
  Snapshot,
} from '@thrasher/shared';

export interface NetHandlers {
  snapshot: (snapshot: Snapshot, serverNow: number) => void;
  roster: (riders: RosterEntry[]) => void;
  hit: (hit: HitEvent) => void;
  respawn: (event: RespawnEvent) => void;
  feed: (event: FeedEvent) => void;
  award: (event: AwardEvent) => void;
  /** `reconnected` is true for every connect after the first. */
  connection: (connected: boolean, reconnected: boolean) => void;
}

/** Socket.IO wrapper with a smoothed server clock estimate for interpolation. */
export class Net {
  private readonly socket: Socket<ServerToClientEvents, ClientToServerEvents>;
  private offset: number | null = null;
  private everConnected = false;
  private dropped = false;

  public constructor(handlers: NetHandlers) {
    this.socket = io({ transports: ['websocket', 'polling'], reconnection: true });
    this.socket.on('connect', () => {
      const again = this.everConnected;
      this.everConnected = true;
      handlers.connection(true, again);
    });
    this.socket.on('disconnect', (reason) => {
      // Socket.IO does not retry after a server-side drop (idle timeout); wait for the player.
      if (reason === 'io server disconnect') this.dropped = true;
      handlers.connection(false, false);
    });
    document.addEventListener('visibilitychange', this.revive);
    window.addEventListener('keydown', this.revive);
    this.socket.on('snap', (snapshot) => {
      const local = performance.now();
      const sample = snapshot.t - local;
      // Track the lowest-latency sample, drifting slowly so clock skew is absorbed.
      this.offset =
        this.offset === null
          ? sample
          : Math.max(sample, this.offset + (sample - this.offset) * 0.02);
      handlers.snapshot(snapshot, this.serverNow());
    });
    this.socket.on('roster', (riders) => handlers.roster(riders));
    this.socket.on('hit', (hit) => handlers.hit(hit));
    this.socket.on('respawn', (event) => handlers.respawn(event));
    this.socket.on('feed', (event) => handlers.feed(event));
    this.socket.on('award', (event) => handlers.award(event));
  }

  public get connected(): boolean {
    return this.socket.connected;
  }

  public serverNow(): number {
    return performance.now() + (this.offset ?? 0);
  }

  public hello(name: string, ride: Ride, token: string | null): Promise<HelloResult> {
    return new Promise((resolve) => {
      let settled = false;
      let sent = false;
      const finish = (result: HelloResult) => {
        if (settled) return;
        settled = true;
        clearTimeout(overall);
        this.socket.off('connect', send);
        // A hello that timed out may still be admitted late; reconnect so the server drops
        // that half-joined rider and a retry starts clean.
        if (!result.ok && sent) this.socket.disconnect().connect();
        resolve(result);
      };
      // Covers the case where the socket never connects at all.
      const overall = setTimeout(() => finish({ ok: false, error: 'invalid' }), 12_000);
      const send = () => {
        sent = true;
        this.socket
          .timeout(8_000)
          .emit('hello', { v: PROTOCOL_VERSION, name, ride, token }, (error, result) => {
            finish(error ? { ok: false, error: 'invalid' } : result);
          });
      };
      if (this.socket.connected) send();
      else this.socket.once('connect', send);
    });
  }

  public sendState(state: RiderState): void {
    if (this.socket.connected) this.socket.volatile.emit('state', state);
  }

  public attack(kind: AttackKind): void {
    this.socket.emit('attack', { kind });
  }

  public ride(ride: Ride): void {
    this.socket.emit('ride', { ride });
  }

  public reset(): void {
    this.socket.emit('reset');
  }

  private readonly revive = (): void => {
    if (this.dropped && document.visibilityState === 'visible') {
      this.dropped = false;
      this.socket.connect();
    }
  };

  public close(): void {
    document.removeEventListener('visibilitychange', this.revive);
    window.removeEventListener('keydown', this.revive);
    this.socket.removeAllListeners();
    this.socket.disconnect();
  }
}
